<template>
  <v-row>
    <v-col cols="12">
      <base-card>
        <v-card-title class="d-flex justify-space-between">
          <v-btn
            class="ma-2"
            color="secondary"
            @click="$router.back()"
            outlined
            dark
          >
            <v-icon>mdi-arrow-left</v-icon>
            Back
          </v-btn>

          <v-btn color="primary" @click="edit()" :disabled="!this.student">
            Save Changes
          </v-btn>
        </v-card-title>
        <v-card-title class="pl-6">Student Courses</v-card-title>
        <div class="pa-5 pl-6">
          <v-select
            v-model="courses"
            :items="availableCourses"
            @change="onCourseChange()"
            item-text="title"
            item-value="id"
            label="Select Courses"
            outlined
            multiple
            chips
            hide-details
          ></v-select>
        </div>
        <v-card-title class="pl-6">Courses</v-card-title>
        <div class="pa-5 pl-6">
          <v-data-table
            item-key="id"
            class="elevation-1 data-table"
            :headers="headers"
            :items="studentCourseDetails || []"
            :disable-filtering="true"
          >
            <template v-slot:item.title="{ item }">
              {{ getTitle(item) }}
            </template>
            <template v-slot:item.is_scorm="{ item }">
              <v-icon v-if="isScorm(item)" color="secondary">mdi-check</v-icon>
            </template>
            <template v-slot:item.certificate_received="{ item }">
              <v-icon v-if="item.certificate_received" color="secondary"
                >mdi-check</v-icon
              >
            </template>
            <template v-slot:item.actions="{ item }">
              <v-menu
                origin="bottom center"
                :offset-x="true"
                :offset-y="true"
                bottom
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="primary"
                    :disabled="!item.certificate_received"
                    v-bind="attrs"
                    v-on="on"
                    small
                  >
                    Manage
                    <v-icon right class="ml-0">mdi-menu-down</v-icon>
                  </v-btn>
                </template>
                <v-list class="py-1" dense>
                  <v-list-item @click="sendOrDownload(item, true)">
                    <v-list-item-icon class="mr-1">
                      <v-icon dense>mdi-download-circle</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        Download Certificate
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item @click="sendOrDownload(item)">
                    <v-list-item-icon class="mr-1">
                      <v-icon dense>mdi-email</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Send Certificate</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </div>
      </base-card>
    </v-col>
  </v-row>
</template>

<script>
import { sendOrDownloadCertificate } from "@/services/courseService";
import { sendOrDownloadScormCertificate } from "@/services/scormService";
import { getEnrolledCourses } from "@/services/studentCoursesService";
import saveAs from "file-saver";
import { difference, each, find, get, map, sortBy } from "lodash";
import { mapActions, mapGetters } from "vuex";
export default {
  metaInfo: {
    title: "Form Student Course",
  },

  data() {
    return {
      loading: false,
      courses: [],
      student: null,
      studentCourseDetails: [],
      studentDetails: {},
      availableCourses: [],
      headers: [
        { text: "Title", value: "title" },
        {
          text: "Certificate Received",
          value: "certificate_received",
          align: "center",
          sortable: false,
        },
        {
          text: "Certificate Received Date",
          value: "certificate_received_date",
        },
        {
          text: "Is Scorm",
          value: "is_scorm",
          align: "center",
          sortable: false,
        },
        { text: "Actions", value: "actions", align: "center", sortable: false },
      ],
    };
  },

  async mounted() {
    const id = this.$route.params.id;

    const { data } = await getEnrolledCourses({ id });
    this.studentCourseDetails = data;

    if (id) {
      this.student = Number(id);

      this.studentDetails = find(this.getStudents, { id: this.student });

      each(
        get(this.getStudentsCourses[this.student], "courses", []),
        (course) => this.courses.push(course.id)
      );

      const coursesAvailable = get(
        this.getCustomerCourses[get(this.studentDetails, "customer_id")],
        "courses"
      );

      this.availableCourses = sortBy(
        map(map(coursesAvailable, "id"), (courseId) =>
          find(this.getCourseLookup, { id: courseId })
        ),
        "title"
      );
    }
  },

  methods: {
    ...mapActions(["addStudentCourse", "deleteStudentCourse", "showSnackbar"]),

    onCourseChange() {
      const list = map(this.courses, (courseId) =>
        find(this.availableCourses, { id: courseId })
      );

      this.courses = map(sortBy(list, "title"), (course) => get(course, "id"));
    },

    async edit() {
      const originalCourses = [];
      each(
        get(this.getStudentsCourses[this.student], "courses", []),
        (course) => originalCourses.push(course.id)
      );

      const removed = difference(originalCourses, this.courses);
      const added = difference(this.courses, originalCourses);

      try {
        // Removed students
        if (removed.length) {
          await this.deleteStudent(removed);
        }

        // Add students
        if (added.length) {
          await this.addStudentCourse({
            courseLookupIds: added,
            studentId: this.student,
          });
        }
        await this.showSnackbar("Student course updated successfully!");
        this.$router.push("/app/student");
      } catch (error) {
        await this.showSnackbar("Something went wrong!");
      }
    },

    async deleteStudent(ids) {
      await this.deleteStudentCourse({
        studentId: this.student,
        courseLookupIds: ids,
      });
    },

    async add() {
      await this.addStudentCourse({
        courseLookupIds: this.courses,
        studentId: this.student,
      });

      this.$router.push("/app/student");
    },

    async sendOrDownload(item, download) {
      const isScorm = this.isScorm(item);

      try {
        const { data } = isScorm
          ? await sendOrDownloadScormCertificate({
              studentId: this.student,
              studentCourseId: item.id,
              courseLookupId: item.course_lookup_id,
              download: download,
            })
          : await sendOrDownloadCertificate({
              studentCourseId: item.id,
              courseId: get(item, "course_lookup.course_id"),
              studentId: this.student,
              download: download,
            });

        if (download) {
          const title = isScorm
            ? get(item, "course_lookup.scorm.title")
            : get(item, "course_lookup.course.description");
          saveAs(data, `${title}_Certificate.pdf`);
        }

        await this.showSnackbar(
          `Certificate ${download ? "downloaded" : "sent"}!`
        );
      } catch (error) {
        console.log(error);
        await this.showSnackbar("Something went wrong!");
      }
    },
  },
  computed: {
    ...mapGetters([
      "getStudents",
      "getCourseLookup",
      "getStudentsCourses",
      "getCustomerCourses",
    ]),

    getTitle() {
      return (item) => {
        const courseLookup = get(item, "course_lookup");
        return (
          get(courseLookup, "course.description") ||
          get(courseLookup, "scorm.title")
        );
      };
    },

    isScorm() {
      return (item) => !!get(item, "course_lookup.scorm");
    },
  },
};
</script>
<style lang="scss" scoped></style>
